/**
 * Product Card Variant Handler
 * Manages swatch interactions and pricing updates on collection pages
 */

;(function () {
  'use strict'

  // ========================================
  // CONSTANTS
  // ========================================
  const SELECTORS = {
    CARD_WRAPPER: '.card-wrapper',
    SWATCH_INPUT: '.swatch-input__input',
    PRICE_CONTAINER: '.price',
    PRICE_REGULAR: '.price__regular .price-item--regular',
    PRICE_SALE: '.price__sale .price-item--sale',
    PRICE_COMPARE_SALE: '.price__sale .price-item--regular',
    PRICE_COMPARE_REGULAR: '.price__regular .variant-item__old-price',
    PRICE_REGULAR_SECTION: '.price__regular',
    PRICE_SALE_SECTION: '.price__sale',
    PRODUCT_LINKS: 'a[href*="/products/"]',
  }

  const DATA_ATTRIBUTES = {
    VARIANT_PRICE: 'variantPrice',
    VARIANT_COMPARE_PRICE: 'variantCompareAtPrice',
    VARIANT_ID: 'variantId',
    VARIANT_IMAGE: 'variantImage',
    VARIANT_HOVER_IMAGE: 'variantHoverImage',
    VARIANT_AVAILABLE: 'variantAvailable',
    TARGET_ID: 'targetId',
    HOVER_TARGET_ID: 'hoverTargetId',
    PRODUCT_URL: 'productUrl',
    PRODUCT_ID: 'productId',
    IS_SALE: 'isSale',
    IS_FINAL_SALE: 'isFinalSale',
  }

  const CSS_CLASSES = {
    PRICE_ON_SALE: 'price--on-sale',
    PRICE_SOLD_OUT: 'price--sold-out',
  }

  // ========================================
  // STATE MANAGEMENT
  // ========================================
  const originalPriceContent = new Map()

  // ========================================
  // UTILITY FUNCTIONS
  // ========================================

  /**
   * Formats price in cents to currency string
   * @param {number} cents - Price in cents
   * @returns {string} Formatted price string
   */
  function formatMoney(cents) {
    try {
      // Try Shopify's theme money format
      if (window.theme?.moneyFormat) {
        return window.theme.moneyFormat.replace(
          '{{amount}}',
          (cents / 100).toFixed(2)
        )
      }

      // Try Shopify global formatter
      if (window.Shopify?.formatMoney) {
        return window.Shopify.formatMoney(cents)
      }

      // Fallback formatting
      return `$${(cents / 100).toFixed(2)}`
    } catch (error) {
      console.warn('Error formatting money:', error)
      return `$${(cents / 100).toFixed(2)}`
    }
  }

  /**
   * Gets product ID from card element
   * @param {HTMLElement} card
   * @returns {string|null}
   */
  function getProductId(card) {
    try {
      return (
        card.querySelector(`[data-${DATA_ATTRIBUTES.PRODUCT_ID}]`)?.dataset[
          DATA_ATTRIBUTES.PRODUCT_ID
        ] ||
        card
          .querySelector(SELECTORS.PRODUCT_LINKS)
          ?.href?.split('/')
          .pop()
          ?.split('?')[0] ||
        null
      )
    } catch (error) {
      console.warn('Error getting product ID:', error)
      return null
    }
  }

  /**
   * Gets currently selected variant ID from card
   * @param {HTMLElement} card
   * @returns {string|null}
   */
  function getCurrentVariantId(card) {
    try {
      return (
        card.querySelector(`${SELECTORS.SWATCH_INPUT}:checked`)?.dataset[
          DATA_ATTRIBUTES.VARIANT_ID
        ] || null
      )
    } catch (error) {
      console.warn('Error getting current variant ID:', error)
      return null
    }
  }

  // ========================================
  // CORE FUNCTIONALITY
  // ========================================

  /**
   * Stores original pricing content for restoration
   */
  function storeOriginalPricing() {
    document
      .querySelectorAll(
        `${SELECTORS.CARD_WRAPPER} ${SELECTORS.PRICE_CONTAINER}`
      )
      .forEach((priceContainer) => {
        try {
          const card = priceContainer.closest(SELECTORS.CARD_WRAPPER)
          if (!card) return

          const productId = getProductId(card)
          const originalVariantId = getCurrentVariantId(card)

          if (productId) {
            // Store the complete innerHTML of the price container
            originalPriceContent.set(productId, {
              completeHTML: priceContainer.innerHTML,
              classList: Array.from(priceContainer.classList),
              originalVariantId,
            })

            console.log(`Stored original pricing for product ${productId}:`, {
              originalVariantId,
              hasContent: !!priceContainer.innerHTML,
            })
          }
        } catch (error) {
          console.warn('Error storing original pricing:', error)
        }
      })
  }

  function selectSaleVariant() {
    document.querySelectorAll(`${SELECTORS.CARD_WRAPPER}`).forEach((card) => {
      const selectSaleVariant =
        card.dataset.selectSaleVariant.toString().trim() == 'true'

      if (selectSaleVariant) {
        const selectedSwatchInput = card.querySelector(
          '.swatch-input__input[checked]'
        )

        const selectedIsSale =
          selectedSwatchInput.dataset?.isSale?.toString()?.trim() != 'true'

        const swatchSaleInputs = [
          ...card.querySelectorAll('.swatch-input__input[data-is-sale="true"]'),
        ]

        const selectedIsFinalSale =
          selectedSwatchInput.dataset?.isFinalSale?.toString()?.trim() != 'true'

        const swatchFinalSaleInputs = [
          ...card.querySelectorAll(
            '.swatch-input__input[data-is-final-sale="true"]'
          ),
        ]

        if (selectedIsSale != 'true' && selectedIsFinalSale != 'true') {
          const firstSaleSwatchInput = swatchSaleInputs?.[0]
          const firstFinalSaleSwatchInput = swatchFinalSaleInputs?.[0]

          if (firstFinalSaleSwatchInput) {
            firstFinalSaleSwatchInput.click()
          } else if (firstSaleSwatchInput) {
            firstSaleSwatchInput.click()
          }
        }

        if (selectedIsSale == 'true' && selectedIsFinalSale != 'true') {
          const firstFinalSaleSwatchInput = swatchFinalSaleInputs?.[0]
          if (firstFinalSaleSwatchInput) {
            firstFinalSaleSwatchInput.click()
          }
        }
      }
    })
  }

  /**
   * Updates product images when swatch changes
   * @param {HTMLElement} swatch - Selected swatch element
   */
  function updateProductImages(swatch) {
    try {
      // Update main image
      const targetId = swatch.dataset[DATA_ATTRIBUTES.TARGET_ID]
      if (targetId) {
        const img = document.getElementById(targetId)
        if (img) {
          const newSrc =
            swatch.dataset[DATA_ATTRIBUTES.VARIANT_IMAGE] ||
            img.dataset.defaultImage
          if (newSrc) {
            img.removeAttribute('srcset')
            img.removeAttribute('sizes')
            img.src = newSrc
          }
        }
      }

      // Update hover image
      const hoverTargetId = swatch.dataset[DATA_ATTRIBUTES.HOVER_TARGET_ID]
      if (hoverTargetId) {
        const hoverImg = document.getElementById(hoverTargetId)
        if (hoverImg) {
          const newSrc =
            swatch.dataset[DATA_ATTRIBUTES.VARIANT_HOVER_IMAGE] ||
            hoverImg.dataset.defaultImage
          if (newSrc) {
            hoverImg.removeAttribute('srcset')
            hoverImg.removeAttribute('sizes')
            hoverImg.src = newSrc
          }
        }
      }
    } catch (error) {
      console.warn('Error updating product images:', error)
    }
  }

  /**
   * Updates product links when swatch changes
   * @param {HTMLElement} card
   * @param {HTMLElement} swatch
   */
  function updateProductLinks(card, swatch) {
    try {
      const productUrl = swatch.dataset[DATA_ATTRIBUTES.PRODUCT_URL]
      const variantId = swatch.dataset[DATA_ATTRIBUTES.VARIANT_ID]
      const productLinks = card.querySelectorAll(SELECTORS.PRODUCT_LINKS)

      if (productUrl) {
        productLinks.forEach((link) => {
          link.href = productUrl
        })
      } else if (variantId) {
        productLinks.forEach((link) => {
          try {
            const url = new URL(link.href)
            url.searchParams.set('variant', variantId)
            link.href = url.toString()
          } catch (urlError) {
            console.warn('Error updating product link:', urlError)
          }
        })
      }
    } catch (error) {
      console.warn('Error updating product links:', error)
    }
  }

  /**
   * Updates product badge when swatch changes
   * @param {HTMLElement} card
   * @param {HTMLElement} swatch
   */
  function updateProductBadge(card, swatch) {
    try {
      const variantAvailable =
        swatch.dataset[DATA_ATTRIBUTES.VARIANT_AVAILABLE] == 'true'
      const isSale = swatch.dataset[DATA_ATTRIBUTES.IS_SALE] == 'true'
      const isFinalSale =
        swatch.dataset[DATA_ATTRIBUTES.IS_FINAL_SALE] == 'true'

      const cardBadgeSoldOutClass = 'card-wrapper__sold-out'
      const cardBadgeSaleClass = 'card-wrapper__sale'
      const cardBadgeFinalSaleClass = 'card-wrapper__final-sale'

      if (!variantAvailable) {
        // Adding Sold Out Badge
        card.classList.remove(cardBadgeSaleClass)
        card.classList.remove(cardBadgeFinalSaleClass)
        if (!card.classList.contains(cardBadgeSoldOutClass)) {
          card.classList.add(cardBadgeSoldOutClass)
        }
        return
      }

      if (isFinalSale) {
        // Adding Final Sale Badge
        card.classList.remove(cardBadgeSoldOutClass)
        card.classList.remove(cardBadgeSaleClass)
        if (!card.classList.contains(cardBadgeFinalSaleClass)) {
          card.classList.add(cardBadgeFinalSaleClass)
        }
        return
      }

      if (isSale) {
        // Adding Sale Badge
        card.classList.remove(cardBadgeSoldOutClass)
        card.classList.remove(cardBadgeFinalSaleClass)
        if (!card.classList.contains(cardBadgeSaleClass)) {
          card.classList.add(cardBadgeSaleClass)
        }
        return
      }

      // Remove Badge
      card.classList.remove(cardBadgeSoldOutClass)
      card.classList.remove(cardBadgeSaleClass)
      card.classList.remove(cardBadgeFinalSaleClass)
    } catch (error) {
      console.warn('Error updating product links:', error)
    }
  }

  /**
   * Restores original pricing format
   * @param {HTMLElement} priceContainer - Price container element
   * @param {Object} originalData - Original pricing data
   */
  function restoreOriginalPricing(priceContainer, originalData) {
    try {
      console.log('Restoring original pricing:', {
        hasCompleteHTML: !!originalData.completeHTML,
        classListLength: originalData.classList.length,
      })

      // Restore the complete HTML structure
      priceContainer.innerHTML = originalData.completeHTML

      // Restore classes
      priceContainer.className = originalData.classList.join(' ')

      console.log('Original pricing restored successfully')
    } catch (error) {
      console.warn('Error restoring original pricing:', error)
    }
  }

  /**
   * Updates pricing with specific variant data
   * @param {HTMLElement} priceContainer
   * @param {number} price
   * @param {number|null} compareAtPrice
   */
  function updateSpecificVariantPricing(priceContainer, price, compareAtPrice) {
    try {
      const priceNumber = parseInt(price)
      const compareAtPriceNumber = compareAtPrice
        ? parseInt(compareAtPrice)
        : null
      const isOnSale =
        compareAtPriceNumber && compareAtPriceNumber > priceNumber

      const formattedPrice = formatMoney(priceNumber)
      const formattedCompareAtPrice = compareAtPriceNumber
        ? formatMoney(compareAtPriceNumber)
        : null

      // Update price classes
      priceContainer.classList.toggle(CSS_CLASSES.PRICE_ON_SALE, isOnSale)
      priceContainer.classList.remove(CSS_CLASSES.PRICE_SOLD_OUT)

      // Clear existing content
      priceContainer.innerHTML = `
        <div class="price__container">
          ${
            isOnSale
              ? createSalePricingHTML(formattedPrice, formattedCompareAtPrice)
              : createRegularPricingHTML(formattedPrice)
          }
        </div>
      `
    } catch (error) {
      console.warn('Error updating specific variant pricing:', error)
    }
  }

  /**
   * Creates HTML for sale pricing
   * @param {string} salePrice
   * @param {string} comparePrice
   * @returns {string}
   */
  function createSalePricingHTML(salePrice, comparePrice) {
    return `
      <div class="price__sale">
        <span class="visually-hidden visually-hidden--inline">Sale price</span>
        <span class="price-item price-item--sale price-item--last">${salePrice}</span>
        <span class="visually-hidden visually-hidden--inline">Regular price</span>
        <span>
          <s class="price-item price-item--regular">${comparePrice}</s>
        </span>
      </div>
    `
  }

  /**
   * Creates HTML for regular pricing
   * @param {string} price
   * @returns {string}
   */
  function createRegularPricingHTML(price) {
    return `
      <div class="price__regular">
        <span class="visually-hidden visually-hidden--inline">Regular price</span>
        <span class="price-item price-item--regular">${price}</span>
      </div>
    `
  }

  /**
   * Main pricing update function
   * @param {HTMLElement} card - Card wrapper element
   * @param {string} price - Variant price in cents
   * @param {string} compareAtPrice - Compare at price in cents
   */
  function updateCardPricing(card, price, compareAtPrice) {
    try {
      const priceContainer = card.querySelector(SELECTORS.PRICE_CONTAINER)
      if (!priceContainer) return

      const productId = getProductId(card)
      const currentVariantId = getCurrentVariantId(card)
      const originalData = originalPriceContent.get(productId)

      console.log('updateCardPricing:', {
        productId,
        currentVariantId,
        originalVariantId: originalData?.originalVariantId,
        hasOriginalData: !!originalData,
        isOriginalVariant: currentVariantId === originalData?.originalVariantId,
        price,
        compareAtPrice,
      })

      // Restore original pricing if returning to original variant
      if (originalData && currentVariantId === originalData.originalVariantId) {
        console.log('Triggering original pricing restoration')
        restoreOriginalPricing(priceContainer, originalData)
        return
      }

      // Update with specific variant pricing
      console.log('Applying specific variant pricing')
      updateSpecificVariantPricing(priceContainer, price, compareAtPrice)
    } catch (error) {
      console.warn('Error updating card pricing:', error)
    }
  }

  /**
   * Handles swatch change events
   * @param {Event} event
   */
  function handleSwatchChange(event) {
    try {
      const swatch = event.target
      if (!swatch.matches(SELECTORS.SWATCH_INPUT)) return

      const card = swatch.closest(SELECTORS.CARD_WRAPPER)
      if (!card) return

      // Update all aspects of the product card
      updateProductImages(swatch)
      updateProductLinks(card, swatch)
      updateProductBadge(card, swatch)

      // Update pricing if price data is available
      const variantPrice = swatch.dataset[DATA_ATTRIBUTES.VARIANT_PRICE]
      const variantCompareAtPrice =
        swatch.dataset[DATA_ATTRIBUTES.VARIANT_COMPARE_PRICE]

      if (variantPrice) {
        updateCardPricing(card, variantPrice, variantCompareAtPrice)
      }
    } catch (error) {
      console.warn('Error handling swatch change:', error)
    }
  }

  // ========================================
  // INITIALIZATION
  // ========================================

  /**
   * Initialize the product card variant handler
   */
  function init() {
    try {
      // Store original pricing data
      storeOriginalPricing()

      // Set up event listeners
      document.body.addEventListener('change', handleSwatchChange)
      selectSaleVariant()

      console.log('Product card variant handler initialized')
    } catch (error) {
      console.error('Error initializing product card variant handler:', error)
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
  } else {
    init()
  }
})()
